import React, { Component, useState, useEffect } from "react";
import { bindActionCreators } from "redux";
import moment from "moment";
import selectAccountFlagsContainer, { canEditReason } from "../selectors/accountFlags";
import * as actions from "../actions/accountFlags";
import { injectStripe } from "react-stripe-elements";
import { connect } from "react-redux";
import ListItem from "@material-ui/core/ListItem";
import Checkbox from "@material-ui/core/Checkbox";
import Button from "../components/Button"
import { ClipLoader } from "react-spinners";
import Snackbar from "../components/Snackbar"
import { confirmationError, confirmationSuccess } from "../styles/common.css";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { KeyboardDatePicker } from "@material-ui/pickers";
import TextField from "@material-ui/core/TextField";

const HIDDEN_FLAG_TYPES = ["DISABLE_PAYMENTS"];
const REASON_FLAG_TYPES = ["STATEMENT_REVIEW"];

const AccountFlagRow = ({ flagType, flag, toggleAccountFlag, adminUser }) => {
    const [expireDate, setExpireDate] = useState(null);
    const [reasonValue, setReasonValue] = useState("");

    useEffect(() => {
        if (flag) {
            setExpireDate(flag.expired ? moment.utc(flag.expired) : null);
            setReasonValue(flag.reason || "");
        } else {
            setExpireDate(null);
            setReasonValue("");
        }
    }, [flag]);

    return (
        <ListItem style={{ padding: "0 19px" }}>
            <Checkbox
                type="checkbox"
                className="checkbox"
                checked={Boolean(
                    flag && flag.action !== "remove"
                )}
                onChange={() => toggleAccountFlag(flagType.ID, expireDate ? expireDate.format('YYYY-MM-DD') : null, "toggleAccountFlag")}
                color={
                    flag && flag.account_flag_id
                        ? "secondary"
                        : "primary"
                }
            />

            <div
                style={{
                    paddingTop: flagType.set_expiry ? 5 : 20,
                    textDecoration:
                        flag &&
                            flag.account_flag_id &&
                            flag.action === "remove"
                            ? "line-through"
                            : "",
                }}
            >
                {`${flagType.ID} - ${flagType.Description}  `}
                {
                    flagType.set_expiry &&
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        margin: "-15px 0px",
                    }}>
                        <span id="date-selector-label" style={{marginRight: "10px"}}>Set Expiry:</span>
                        <KeyboardDatePicker
                            margin="normal"
                            format="MM/dd/yyyy"
                            placeholder="MM/dd/yyyy"
                            KeyboardButtonProps={{
                                "aria-label": "change date",
                            }}
                            onChange={date => {
                                let value = date !== null ? moment.utc(date) : null
                                setExpireDate(value)
                                toggleAccountFlag(flagType.ID, value ? value.format('YYYY-MM-DD') : null, "expirationDateUpdated")
                            }}
                            value={(expireDate && expireDate.format("l LT")) || null}
                        />
                    </div>
                }
                {
                    REASON_FLAG_TYPES.includes(flagType.ID) &&
                    <div style={{ marginTop: "-4px", marginBottom: "5px", maxWidth: "400px" }}>
                        <TextField
                            label="Reason"
                            fullWidth
                            multiline
                            inputProps={{ maxLength: 500 }}
                            disabled={!canEditReason(adminUser) || !(flag && flag.action !== "remove")}
                            value={reasonValue}
                            onChange={(e) => {
                                // dispatch on change, not just blur - otherwise a click on
                                // the still-disabled Save button is swallowed before the
                                // blur it triggers has a chance to enable it.
                                const { value } = e.target;
                                setReasonValue(value);
                                toggleAccountFlag(flagType.ID, null, "reasonUpdated", value);
                            }}
                            onBlur={() => {
                                const trimmed = reasonValue.trim();
                                if (trimmed !== reasonValue) {
                                    setReasonValue(trimmed);
                                }
                                if (trimmed !== (flag?.reason || "")) {
                                    toggleAccountFlag(flagType.ID, null, "reasonUpdated", trimmed);
                                }
                            }}
                        />
                    </div>
                }
            </div>
        </ListItem>
    );
};

const AccountFlagList = ({
    category,
    selectedAccountFlags,
    accountFlagTypes,
    isLoading,
    sendAccountsFlagRequest,
    toggleAccountFlag,
    cancelChanges,
    userAccountFlagsChanged,
    expandedView,
    toggleCategoryView,
    adminUser,
}) => {
    const count =
        selectedAccountFlags &&
        selectedAccountFlags.filter((fa) => {
            const c = accountFlagTypes.find((ft) => {
                return ft.ID === fa.flag_type && fa.action !== "remove";
            });
            return c && c.Category === category;
        }).length;

    return (
        <div>
            <hr style={{ margin: "0 25px" }} />
            <div style={{ padding: "5px", marginLeft: "25px" }}>
                {category != null && category.replace("_", " ")} ({count})
                <div style={{ float: "right", paddingRight: "25px" }}>
                    {!expandedView && (
                        <KeyboardArrowDownIcon
                            onClick={() => {
                                toggleCategoryView(category);
                            }}
                        />
                    )}
                    {expandedView && (
                        <KeyboardArrowUpIcon
                            onClick={() => {
                                toggleCategoryView(category);
                            }}
                        />
                    )}
                </div>
            </div>

            {isLoading && expandedView && (
                <div style={{ textAlign: "center" }}>
                    <ClipLoader
                        sizeUnit="px"
                        size={150}
                        color="#ED0874"
                        loading
                    />
                </div>
            )}

            {!isLoading &&
                expandedView &&
                accountFlagTypes.map((f) => {
                    const flag = selectedAccountFlags.find(
                        (fa) => fa.flag_type === f.ID
                    );
                    return (
                        <AccountFlagRow
                            key={f.ID}
                            flagType={f}
                            flag={flag}
                            toggleAccountFlag={toggleAccountFlag}
                            adminUser={adminUser}
                        />
                    );
                })}
            {!isLoading && expandedView && (
                <div>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={async () => await sendAccountsFlagRequest()}
                        style={{ margin: 20 }}
                        disabled={!userAccountFlagsChanged}
                    >
                        Save
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={async () => await cancelChanges()}
                        disabled={!userAccountFlagsChanged}
                    >
                        Cancel
                    </Button>
                </div>
            )}
        </div>
    );
};

const hideAccountFlag = (flagID, adminUser) => {
    // hide all hidden flags for non super users
    const isHiddenFlagType = HIDDEN_FLAG_TYPES.includes(flagID);
    const isSuperUser = adminUser?.roles?.includes('super') ?? false;
    if (isSuperUser) {
        return false;
    }
    return isHiddenFlagType;
}

class AccountsFlag extends Component {
    componentDidMount() {
        const {
            initialized,
            fetchAccountFlagTypes,
            fetchAccountFlags,
        } = this.props;
        if (!initialized) {
            fetchAccountFlagTypes();
            fetchAccountFlags();
        }
    }

    componentWillReceiveProps() { }

    render() {
        const {
            isLoading,
            accountFlagTypes,
            selectedAccountFlags,
            sendAccountsFlagRequest,
            toggleAccountFlag,
            userAccountFlagsChanged,
            cancelChanges,
            successMessage,
            errorMessage,
            showSnackBar,
            closeSnackbar,
            toggleCategoryView,
            expandedViews,
            adminUser,
        } = this.props;
        return (
            <div
                style={{
                    border: "1px solid lightgrey",
                    marginTop: "20px",
                    marginLeft: "25px",
                }}
            >
                <h4 style={{ marginLeft: "25px" }}>Account Flags</h4>
                {[...new Set([...accountFlagTypes.map((t) => t.Category)])].map(
                    (category, i) => (
                        <AccountFlagList
                            key={i}
                            category={category}
                            selectedAccountFlags={selectedAccountFlags}
                            accountFlagTypes={accountFlagTypes.filter(
                                (t) => t.Category === category && !hideAccountFlag(t.ID, adminUser)
                            )}
                            isLoading={isLoading}
                            sendAccountsFlagRequest={sendAccountsFlagRequest}
                            expandedView={
                                expandedViews.indexOf(category) !== -1
                            }
                            toggleCategoryView={toggleCategoryView}
                            toggleAccountFlag={toggleAccountFlag}
                            userAccountFlagsChanged={userAccountFlagsChanged}
                            cancelChanges={cancelChanges}
                            adminUser={adminUser}
                        />
                    )
                )}
                <Snackbar
                    id="message-id"
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                    open={showSnackBar}
                    onClose={closeSnackbar}
                    className={successMessage ? confirmationSuccess : confirmationError}
                    message={successMessage || errorMessage}
                />
            </div>
        );
    }
}

const mapStateToProps = selectAccountFlagsContainer();

const mapDispatchToProps = (dispatch) => ({
    ...bindActionCreators(actions, dispatch),
    dispatch,
});

const ConnectedAccountsFlag = connect(
    mapStateToProps,
    mapDispatchToProps
)(AccountsFlag);

export default injectStripe(ConnectedAccountsFlag);
